import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, ArrowLeft } from "lucide-react";
import { useState } from "react";

const IOS_URL = "https://apps.apple.com/in/app/zoku-track-calories/id6762446883";

export default function GroupInvite() {
  const { code = "" } = useParams();
  const [copied, setCopied] = useState(false);

  const cleanCode = (code || "").trim().toUpperCase();
  const installLink = `${IOS_URL}?referral_code=${encodeURIComponent(cleanCode)}`;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(cleanCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-zoku-bg text-zoku-ink flex flex-col" data-testid="group-invite-page">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zoku-ink2 hover:text-zoku-ink" data-testid="group-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <Link to="/" className="font-heading text-xl font-bold tracking-tight" data-testid="group-brand">
          Zoku
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-xl text-center"
        >
          <div className="inline-flex items-center bg-zoku-orange/10 text-zoku-orange text-[11px] font-bold tracking-widest uppercase rounded-full px-3 py-1">
            You're invited
          </div>

          <h1 className="mt-6 font-heading text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
            Join the group on{" "}
            <span className="font-editorial italic text-zoku-orange">Zoku.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-zoku-ink2 max-w-md mx-auto leading-relaxed">
            Install Zoku and enter the invite code below to join your group.
          </p>

          <div className="mt-10 rounded-3xl bg-white border border-stone-200 shadow-sm p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-widest text-zoku-ink3 font-semibold">
              Invite code
            </div>
            <div className="mt-3 flex items-center justify-center gap-3">
              <code
                className="font-mono text-3xl md:text-4xl font-bold tracking-[0.2em] text-zoku-ink select-all"
                data-testid="group-code"
              >
                {cleanCode || "—"}
              </code>
              {cleanCode && (
                <button
                  onClick={copyCode}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-stone-200 hover:bg-stone-50 active:scale-95 transition"
                  aria-label="Copy code"
                  data-testid="group-copy-btn"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-zoku-ink2" />
                  )}
                </button>
              )}
            </div>

            <a
              href={installLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-1.5 bg-zoku-ink text-white rounded-full px-7 py-4 font-semibold hover:bg-stone-800 transition-all active:scale-95 shadow-lg"
              data-testid="group-install-btn"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/8gdpr0ux_apple-icon-4.png"
                alt=""
                className="w-6 h-6 object-contain"
                style={{ filter: "invert(1) brightness(2)" }}
              />
              Download Zoku
            </a>

            <p className="mt-4 text-xs text-zoku-ink3">
              Already have Zoku? This link will open your app.
            </p>
          </div>

          <ol className="mt-10 text-left space-y-3 max-w-md mx-auto">
            {[
              "Tap Download Zoku and install from the App Store.",
              "Open Zoku and tap Join group.",
              `Enter the invite code: ${cleanCode || "—"}`,
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zoku-ink2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-zoku-orange/10 text-zoku-orange font-bold text-xs flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </motion.div>
      </main>

      <footer className="px-6 py-8 text-center text-xs text-zoku-ink3">
        © 2026 Thriveage Wellness Pvt Ltd · <Link to="/privacy" className="hover:text-zoku-ink">Privacy</Link> · <Link to="/terms" className="hover:text-zoku-ink">Terms</Link>
      </footer>
    </div>
  );
}
